import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../ui/card';
import { Users, RefreshCw, MessageCircle } from 'lucide-react';
import Pagination from '../shared/Pagination';
import { API } from '../../../lib/api';

const PAGE_SIZE = 10;

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const LeadsTab = ({ partners, getAuthHeaders }) => {
  const [selectedPartner, setSelectedPartner] = useState('all');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchLeads = useCallback(async (partnerId) => {
    setLoading(true);
    setCurrentPage(1);
    try {
      if (partnerId === 'all') {
        const results = await Promise.all(
          partners.map(p => axios.get(`${API}/partners/${p.id}/leads`, getAuthHeaders()).then(r => r.data))
        );
        setLeads(results.flat().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      } else {
        const res = await axios.get(`${API}/partners/${partnerId}/leads`, getAuthHeaders());
        setLeads(res.data);
      }
    } catch {
      toast.error('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  }, [partners, getAuthHeaders]); // eslint-disable-line

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (partners.length > 0) fetchLeads(selectedPartner); }, [selectedPartner, partners.length]);

  const toUTC = (iso) => {
    if (!iso) return null;
    return iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
  };

  const formatDate = (iso) => {
    const utc = toUTC(iso);
    if (!utc) return '—';
    return new Date(utc).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const timeAgo = (iso) => {
    const utc = toUTC(iso);
    if (!utc) return '';
    const diff = Date.now() - new Date(utc).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
    if (diff < 60000) return 'agora mesmo';
    if (mins < 60) return `há ${mins} min`;
    if (hours < 24) return `há ${hours}h`;
    if (days < 7) return `há ${days} dia${days > 1 ? 's' : ''}`;
    if (weeks < 5) return `há ${weeks} semana${weeks > 1 ? 's' : ''}`;
    if (months < 12) return `há ${months} mês${months > 1 ? 'es' : ''}`;
    return `há ${years} ano${years > 1 ? 's' : ''}`;
  };

  const formatWA = (n) => {
    const d = String(n).replace(/\D/g, '');
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 13) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
    return n;
  };

  const getPartnerName = (lead) => {
    const p = partners.find(p => p.id === lead.partner_id);
    return p?.name || lead.partner_slug;
  };

  const getWhatsappUrl = (lead) => {
    const cleanPhone = String(lead.whatsapp).replace(/\D/g, '');
    const firstName = lead.name ? lead.name.trim().split(' ')[0] : '';
    const greeting = firstName ? `Olá, ${firstName}! Tudo bem?` : 'Olá! Tudo bem?';
    const partner = getPartnerName(lead);
    
    const v = lead.investment_value ?? 0;
    const invCash = formatCurrency(v);
    
    const downPayment = v * 0.10;
    const installment = ((v * 0.90) * 1.12) / 12;
    const invFinanced = `${formatCurrency(downPayment)} de entrada + 12x de ${formatCurrency(installment)}`;
    const investmentText = `${invFinanced} ou ${invCash} à vista`;
    
    const savings = formatCurrency(lead.monthly_savings);

    const message = `${greeting}

Vi que você fez uma simulação no site da 3M Solar pelo QR Code do parceiro *${partner}*, com estimativa de investimento de *${investmentText}* e economia de *${savings}/mês*.

Você ainda não iniciou o atendimento pelo WhatsApp. Posso te ajudar com alguma dúvida sobre esses valores?`;
    return `https://wa.me/55${cleanPhone}?text=${encodeURIComponent(message)}`;
  };

  const totalPages = Math.ceil(leads.length / PAGE_SIZE);
  const paginatedLeads = leads.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-gray-800">Leads Capturados ({leads.length})</h2>
        <div className="flex items-center gap-3">
          <select
            value={selectedPartner}
            onChange={(e) => setSelectedPartner(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">Todos os parceiros</option>
            {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={() => fetchLeads(selectedPartner)} className="p-2 text-gray-400 hover:text-orange-600 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : leads.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nenhum lead capturado ainda.</p>
        </Card>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 border-b">
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Parceiro</th>
                  <th className="px-4 py-3">Nome</th>
                  <th className="px-4 py-3 whitespace-nowrap">WhatsApp</th>
                  <th className="px-4 py-3 hidden md:table-cell">Faixa</th>
                  <th className="px-4 py-3 hidden md:table-cell">Investimento</th>
                  <th className="px-4 py-3 hidden md:table-cell">Economia/mês</th>
                  <th className="px-4 py-3 md:hidden">Detalhes</th>
                  <th className="px-4 py-3 text-green-600 hidden md:table-cell">Retorno %</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeads.map((lead, i) => (
                  <tr key={lead.id || i} className="border-b last:border-0 hover:bg-orange-50/40 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-xs">
                      <span className="block text-orange-500 font-medium mb-0.5">{timeAgo(lead.created_at)}</span>
                      <span className="text-gray-400">{formatDate(lead.created_at)}</span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <span>{getPartnerName(lead)}</span>
                      {lead.helper_name ? (
                        <span className="block text-xs text-gray-500 mt-1">
                          Ajudante: <span className="font-medium text-gray-600">{lead.helper_name}</span>
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{lead.name || <span className="text-gray-300 italic">—</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-gray-700 text-xs">{formatWA(lead.whatsapp)}</span>
                        <a
                          href={getWhatsappUrl(lead)}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white text-xs font-medium px-2 py-1.5 rounded-lg transition-colors shrink-0"
                          title="Abrir WhatsApp"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">WhatsApp</span>
                        </a>
                      </div>
                    </td>
                    {/* Desktop-only columns */}
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full font-medium">
                        {lead.energy_range_label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-800 hidden md:table-cell">
                      {formatCurrency(lead.investment_value)}
                    </td>
                    <td className="px-4 py-3 font-semibold text-orange-600 hidden md:table-cell">
                      {formatCurrency(lead.monthly_savings)}/mês
                    </td>
                    {/* Mobile condensed details column */}
                    <td className="px-4 py-3 md:hidden">
                      <div className="flex flex-col gap-0.5 text-xs">
                        <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium w-fit">
                          {lead.energy_range_label}
                        </span>
                        <span className="font-semibold text-gray-800">{formatCurrency(lead.investment_value)}</span>
                        <span className="text-orange-600 font-medium">{formatCurrency(lead.monthly_savings)}/mês</span>
                      </div>
                    </td>
                    {/* Retorno — desktop only */}
                    <td className="px-4 py-3 font-bold text-green-600 hidden md:table-cell">
                      {lead.monthly_return_pct?.toFixed(1).replace('.', ',')}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={leads.length}
            pageSize={PAGE_SIZE}
          />
        </div>
      )}
    </div>
  );
};

export default LeadsTab;
